// Pure helpers for the admin VideoUpload page. Kept in a separate
// module so the page component stays a single default export and the
// helpers are trivially unit-testable.

export const UploadType = Object.freeze({
  FILE_UPLOAD: "file",
  YOUTUBE_UPLOAD: "youtube",
});

// Treat empty / NaN / 0 lengths as "not yet known". Used to gate the
// Save button so YouTube uploads cannot be persisted before
// getVideoInfo resolves -- the previous code wrote rows with
// `videoLength: ""` to the backend (Khubaib bug #1).
export const isValidLength = (raw) => {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0;
};

// Extract the 11-ish char YouTube video id from any common URL form
// (watch?v=, youtu.be/, /embed/, /shorts/) so different links to the same
// video collide for duplicate detection. Returns null for non-YouTube urls.
export const getYoutubeId = (url) => {
  if (!url || typeof url !== "string") return null;
  const patterns = [
    /[?&]v=([^&]+)/,
    /youtu\.be\/([^?&/]+)/,
    /\/embed\/([^?&/]+)/,
    /\/shorts\/([^?&/]+)/,
  ];
  for (const re of patterns) {
    const match = url.match(re);
    if (match && match[1]) return match[1];
  }
  return null;
};

// Canonical key used to decide whether two rows are "the same video".
// YouTube rows dedupe by video id; anything else by its trimmed,
// lower-cased url. Returns "" when no usable key can be derived.
export const getVideoDedupeKey = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  const ytId = getYoutubeId(trimmed);
  return ytId ? `yt:${ytId}` : `url:${trimmed.toLowerCase()}`;
};

// Stable signature for a picked file. A new file upload gets a unique
// randomUUID S3 url at save time, so urls never collide -- two uploads of
// the same file would otherwise both be stored. We key file rows off the
// file's name + byte size instead, which catches the reported repro
// (picking the same file twice). Returns "" when no usable signature can
// be derived.
export const getFileSignature = (file) => {
  if (!file || typeof file.name !== "string") return "";
  const name = file.name.trim().toLowerCase();
  if (!name || typeof file.size !== "number" || !(file.size > 0)) return "";
  return `${name}::${file.size}`;
};

// Dedupe key for an existing library row. File rows carry a `fileSignature`
// (stored at upload time); fall back to the url-based key for YouTube rows
// and older file rows that predate signatures.
export const getRowDedupeKey = (video) => {
  if (video && video.fileSignature) return `file:${video.fileSignature}`;
  return getVideoDedupeKey(video?.url);
};

// Returns the first already-uploaded video matching the candidate, or null.
// Pass `fileSignature` (from getFileSignature) for file uploads; otherwise
// matching falls back to the `url` (YouTube id / trimmed url).
export const findDuplicateVideo = (videos, url, fileSignature) => {
  const key = fileSignature ? `file:${fileSignature}` : getVideoDedupeKey(url);
  if (!key) return null;
  return (videos || []).find((v) => getRowDedupeKey(v) === key) || null;
};

export const isSaveReady = (
  formData,
  uploadType,
  selectedVideo,
  selectedFile,
) => {
  // Editing an existing row only mutates `category`, so we only require
  // a non-empty category there.
  if (selectedVideo?.id !== undefined) {
    return (formData?.category || "").trim().length > 0;
  }
  const hasTitle = (formData?.videoTitle || "").trim().length > 0;
  const hasCategory = (formData?.category || "").trim().length > 0;
  const hasLength = isValidLength(formData?.videoLength);
  if (!hasTitle || !hasCategory || !hasLength) return false;
  if (uploadType === UploadType.YOUTUBE_UPLOAD) {
    return (formData?.youtubeUrl || "").trim().length > 0;
  }
  // For a new file upload the file is only sent to storage on save, so
  // `videoUrl` is empty until then -- gate on the picked file instead.
  // (Fall back to videoUrl so a pre-populated row still validates.)
  return !!selectedFile || (formData?.videoUrl || "").trim().length > 0;
};

// Builds the alert message shown when a save fails. The File Upload
// workflow has no URL field, so the old blanket "check the URL" text was
// wrong and confusing there (Khubaib bug). For file uploads we surface the
// specific stage error and never mention a URL; only the YouTube path,
// which actually has a URL field, keeps the URL hint.
export const uploadFailMessage = (uploadType, err) => {
  if (uploadType === UploadType.FILE_UPLOAD) {
    return (
      (err && err.message) ||
      "Could not upload the video file. Please try again."
    );
  }
  return "Could not save the video. Please check the URL and try again.";
};
